import {
  createTenantRequestSchema,
  DEFAULT_STORE_DESIGN,
  MAX_PRODUCT_EXCLUSIONS,
  type CreateTenantResponse,
  type StoreTemplate,
} from '@avrahami/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { keys, platformApi, provisioningQuery, tenantQuery } from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { ManageStore } from '../components/ManageStore';
import { CUSTOMER_TAKEN, customerRefusal } from '../components/StoreCustomerCard';
import { StoreProductsTable } from '../components/StoreProductsTable';
import { TemplatePicker } from '../components/StoreTemplates';
import { he } from '../i18n/he';
import { applyVisibility, exclusionCountText, type PickedProduct } from '../lib/exclusions';
import { errorMessage, ownerInvitationLink } from '../lib/format';
import {
  Alert,
  Button,
  Card,
  CopyField,
  fill,
  invalidFields,
  isApiError,
  PageHeader,
  TextField,
} from '@avrahami/ui';

type FieldName = 'displayName' | 'slug' | 'ownerName' | 'ownerEmail' | 'customerNumber';

/** Maps a validation path (`owner.email`) to the form field that shows it. */
const FIELD_OF_PATH: Readonly<Record<string, FieldName>> = {
  displayName: 'displayName',
  slug: 'slug',
  'owner.displayName': 'ownerName',
  'owner.email': 'ownerEmail',
  customerNumber: 'customerNumber',
};

export function TenantCreatePage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Record<FieldName, string>>({
    displayName: '',
    slug: '',
    ownerName: '',
    ownerEmail: '',
    customerNumber: '',
  });
  const [invalid, setInvalid] = useState<ReadonlySet<FieldName>>(new Set());
  // The store's design template (adr/0034); its colours and the rest are set on its design page.
  const [template, setTemplate] = useState<StoreTemplate>(DEFAULT_STORE_DESIGN.template);
  // Catalog products the new store does not show; the table reads the master catalog.
  const [excluded, setExcluded] = useState<readonly PickedProduct[]>([]);
  const [selected, setSelected] = useState<readonly PickedProduct[]>([]);
  const session = useQuery(sessionQuery);
  const canPick =
    session.data !== undefined && hasCapability(session.data, 'platform.catalog.read');
  const create = useMutation({
    mutationFn: platformApi.createTenant,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.tenants }),
  });

  const bind = (field: FieldName) => ({
    value: form[field],
    error: invalid.has(field) ? he.common.invalidField : undefined,
    onChange: (event: { target: { value: string } }) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    },
  });

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    // The same schema the API validates with: most mistakes never leave the browser.
    const parsed = createTenantRequestSchema.safeParse({
      slug: form.slug,
      displayName: form.displayName,
      owner: { email: form.ownerEmail, displayName: form.ownerName },
      template,
      customerNumber: form.customerNumber,
      ...(excluded.length === 0
        ? {}
        : { excludedProductIds: excluded.map((product) => product.id) }),
    });
    if (!parsed.success) {
      const fields = parsed.error.issues.flatMap((issue) => {
        const field = FIELD_OF_PATH[issue.path.join('.')];
        return field === undefined ? [] : [field];
      });
      setInvalid(new Set(fields));
      return;
    }
    setInvalid(new Set());
    create.mutate(parsed.data);
  };

  if (create.data !== undefined) return <Created result={create.data} />;

  const serverInvalid = invalidFields(create.error);
  const tooManyExcluded = excluded.length > MAX_PRODUCT_EXCLUSIONS;
  // A chosen product left the catalog meanwhile: the whole request is refused.
  const excludedInvalid =
    isApiError(create.error, 'BAD_REQUEST') &&
    create.error.details.some((detail) => detail.path.startsWith('excludedProductIds'));
  return (
    <>
      <PageHeader title={he.createTenant.title} />
      <Card>
        <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
          <div className="grid max-w-xl gap-4">
            <TextField
              label={he.createTenant.displayName}
              name="displayName"
              required
              {...bind('displayName')}
            />
            <TextField
              label={he.createTenant.slug}
              name="slug"
              required
              className="ltr"
              {...bind('slug')}
              error={
                invalid.has('slug') || serverInvalid.has('slug')
                  ? he.common.invalidField
                  : undefined
              }
            />
            <TextField
              label={he.createTenant.ownerName}
              name="ownerName"
              required
              {...bind('ownerName')}
            />
            <TextField
              label={he.createTenant.ownerEmail}
              name="ownerEmail"
              type="email"
              required
              className="ltr"
              {...bind('ownerEmail')}
            />
            <TextField
              label={he.createTenant.customerNumber}
              name="customerNumber"
              required
              autoComplete="off"
              className="ltr"
              hint={he.createTenant.customerNumberHint}
              {...bind('customerNumber')}
            />
          </div>
          <fieldset className="flex min-w-0 flex-col gap-3 rounded-md border border-slate-200 p-4">
            <legend className="px-1 text-sm font-semibold text-slate-900">
              {he.createTenant.template}
            </legend>
            <p className="text-sm text-slate-600">{he.createTenant.templateHint}</p>
            <TemplatePicker
              value={template}
              onChange={setTemplate}
              disabled={create.isPending}
              name="new-store-template"
            />
          </fieldset>
          {canPick ? (
            <fieldset className="flex min-w-0 flex-col gap-3 rounded-md border border-slate-200 p-4">
              <legend className="px-1 text-sm font-semibold text-slate-900">
                {he.createTenant.exclusions}
              </legend>
              <p className="text-sm text-slate-600">{he.createTenant.exclusionsHint}</p>
              <p className="text-sm font-medium text-slate-900" data-testid="exclusion-count">
                {excluded.length === 0 ? he.exclusions.empty : exclusionCountText(excluded.length)}
              </p>
              {tooManyExcluded ? (
                <div data-testid="exclusion-over-limit">
                  <Alert tone="warning">
                    {fill(he.createTenant.exclusionsTooMany, { max: MAX_PRODUCT_EXCLUSIONS })}
                  </Alert>
                </div>
              ) : null}
              <StoreProductsTable
                name="store-products:new"
                hidden={excluded}
                selected={selected}
                onSelect={setSelected}
                onChange={(change) => {
                  // Nothing is sent before the store is created: the choice is only kept.
                  setExcluded((current) => applyVisibility(current, change));
                  setSelected([]);
                }}
                disabled={create.isPending}
              />
            </fieldset>
          ) : null}
          {create.isError ? (
            <Alert>
              {excludedInvalid
                ? he.createTenant.exclusionsInvalid
                : isApiError(create.error, 'CONFLICT') && create.error.message === CUSTOMER_TAKEN
                  ? he.tenant.customerNumberTaken
                  : (customerRefusal(create.error) ?? errorMessage(create.error))}
            </Alert>
          ) : null}
          <div>
            <Button type="submit" busy={create.isPending} disabled={tooManyExcluded}>
              {he.createTenant.submit}
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}

function Created({ result }: { result: CreateTenantResponse }) {
  const session = useQuery(sessionQuery);
  const canManage =
    session.data !== undefined && hasCapability(session.data, 'platform.tenants.manage');
  // The run polls by itself while the worker is on it. Once it stops working (waiting for the
  // copy, done or failed) the store's status is read again: the run opens the admin
  // (PROVISIONING → ACTIVE) before that.
  const run = useQuery({ ...provisioningQuery(result.tenant.id), enabled: canManage });
  const runStatus = run.data?.run?.status;
  const settled = runStatus !== undefined && runStatus !== 'PENDING' && runStatus !== 'RUNNING';
  const tenant = useQuery({ ...tenantQuery(result.tenant.id), enabled: canManage && settled });
  const status = tenant.data?.tenant.status ?? result.tenant.status;
  const hostname = result.domains
    .find((domain) => domain.isPrimary)
    ?.url.replace(/^https:\/\//, '');
  return (
    <>
      <PageHeader title={he.createTenant.created} />
      <Card title={result.tenant.displayName}>
        <div className="flex max-w-2xl flex-col gap-4">
          {hostname === undefined ? null : (
            <p className="text-sm">
              {he.createTenant.address}: <span className="ltr font-mono">{hostname}</span>
            </p>
          )}
          <Alert tone="info">{he.createTenant.provisioning}</Alert>
          {result.owner.invitation === null ? (
            <Alert tone="info">{he.createTenant.existingOwner}</Alert>
          ) : (
            <div className="flex flex-col gap-2">
              <h3 className="font-semibold">{he.createTenant.inviteTitle}</h3>
              <p className="text-sm text-slate-600">{he.createTenant.inviteBody}</p>
              <CopyField
                label={he.createTenant.inviteTitle}
                value={ownerInvitationLink(result.owner.invitation.token)}
                testId="invitation-link"
              />
            </div>
          )}
          <div className="flex flex-wrap items-start gap-4">
            {!canManage ? null : status === 'ACTIVE' || status === 'SUSPENDED' ? (
              <ManageStore tenantId={result.tenant.id} align="start" />
            ) : status === 'PROVISIONING' ? (
              <div className="flex flex-col gap-1">
                <Button tone="secondary" disabled data-testid="manage-store-pending">
                  {he.tenant.manage}
                </Button>
                <p className="text-xs text-slate-500">
                  {runStatus === 'FAILED'
                    ? he.createTenant.manageFailed
                    : he.createTenant.manageWhenReady}
                </p>
              </div>
            ) : null}
            <Link
              to="/tenants/$tenantId"
              params={{ tenantId: result.tenant.id }}
              className="py-2 text-brand-700 underline"
            >
              {he.createTenant.open}
            </Link>
          </div>
        </div>
      </Card>
    </>
  );
}
